import { Navigate, useParams, useSearchParams } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useLaden } from '../../hooks'
import Meldungen from '../../komponenten/Meldungen'
import NotierungsTabelle from '../../komponenten/NotierungsTabelle'
import { datum, eurT, liefermonat, tagMonat, wochentag } from '../../format'
import { notierungTabellePfad, type NotierungAuswahl } from '../../pfade'
import type { NotierungPreisspanne, NotierungTabelle } from '../../typen'

/**
 * Die Tabelle einer Gruppe (ein Reiter je Gruppe, wie je Excel-Mappe ein Fenster) zum Stichtag. Ohne
 * Stichtag zeigt sie den letzten Tag mit Notierungen. Vor und zurück geht Tag für Tag über die Tage,
 * an denen etwas kam - nicht über Kalendertage, an denen die Tabelle gleich bliebe.
 *
 * `?spalte=<quelle_id>&monate=<JJJJ-MM-01,…>` (Klick im Verlauf, Einkaufssignal auf „Heute“) hebt eine Spalte
 * und Liefermonate hervor; der Stichtag lässt sich dabei weiter wechseln.
 */
export default function TabelleSeite() {
  const { gruppe } = useParams()
  const [suche, setSuche] = useSearchParams()
  const am = suche.get('am') ?? undefined
  const spalte = Number(suche.get('spalte')) || null
  const monateParameter = suche.get('monate') ?? ''
  const auswahl: NotierungAuswahl | null = spalte ? { spalte, monate: monateParameter.split(',').filter(Boolean) } : null
  const { daten: gruppen, fehler: gruppenFehler } = useLaden(() => notierungenApi.gruppen(), [], { wenn: !gruppe })
  const { daten: tabelle, fehler, laedt } = useLaden(() => notierungenApi.tabelle(gruppe ?? '', am), [gruppe, am], { wenn: Boolean(gruppe) })

  if (!gruppe) {
    if (gruppen && gruppen.length > 0) return <Navigate to={notierungTabellePfad(gruppen[0].gruppe)} replace />
    return (
      <>
        <h2>Tabelle</h2>
        <Meldungen fehler={gruppenFehler} />
        {gruppen && <p className="leer">Noch keine Rohstoffe angelegt. Unter Stammdaten · Rohstoffe beginnt die Tabelle.</p>}
      </>
    )
  }

  function setzen(werte: Record<string, string | null>) {
    const neu = new URLSearchParams(suche)
    for (const [name, wert] of Object.entries(werte)) {
      if (wert) neu.set(name, wert)
      else neu.delete(name)
    }
    setSuche(neu, { replace: true })
  }

  return (
    <>
      <div className="leiste">
        <h2>{gruppe}</h2>
        {tabelle && <Stichtagleiste tabelle={tabelle} onWahl={(tag) => setzen({ am: tag })} />}
      </div>
      <Meldungen fehler={fehler} />
      {laedt && !tabelle && <p className="hinweis">Wird geladen.</p>}
      {tabelle && (
        <>
          <p className="hinweis nz-legende">
            Minus heißt günstiger als am Vortag (grün), Plus teurer (rot). <b>Fett hinterlegt</b> ist der Bestpreis der Zeile
            je Rohstoff und Qualität, mit Transport gerechnet. Grau: Wert von einem früheren Tag.
          </p>
          {auswahl && <AuswahlZeile tabelle={tabelle} auswahl={auswahl} onAufheben={() => setzen({ spalte: null, monate: null })} />}
          <NotierungsTabelle tabelle={tabelle} auswahl={auswahl} />
          <Preisspannen spannen={tabelle.preisspannen} />
        </>
      )}
    </>
  )
}

/** ‹ früherer Tag · Datum · späterer Tag › · letzter Tag - über die Tage, an denen notiert wurde. */
function Stichtagleiste({ tabelle, onWahl }: { tabelle: NotierungTabelle; onWahl: (tag: string | null) => void }) {
  const tage = tabelle.tage // absteigend
  // Der nächstältere und nächstjüngere Notierungstag zum Stichtag - auch wenn der Stichtag selbst keiner ist.
  const frueher = tage.find((t) => t < tabelle.am) ?? null
  const spaeter = [...tage].reverse().find((t) => t > tabelle.am) ?? null
  const letzter = tage[0] ?? null
  return (
    <div className="leiste stichtag-leiste" style={{ marginBottom: 0 }}>
      <button disabled={!frueher} onClick={() => onWahl(frueher)} title={frueher ? `Notierungen vom ${datum(frueher)}` : 'kein früherer Tag'}>
        ‹ {frueher ? tagMonat(frueher) : 'früher'}
      </button>
      <label className="hinweis">
        Stichtag{' '}
        <input type="date" value={tabelle.am} max={letzter ?? undefined} onChange={(e) => e.target.value && onWahl(e.target.value)} />
      </label>
      <span className="hinweis">{wochentag(tabelle.am)}</span>
      <button disabled={!spaeter} onClick={() => onWahl(spaeter)} title={spaeter ? `Notierungen vom ${datum(spaeter)}` : 'kein späterer Tag'}>
        {spaeter ? tagMonat(spaeter) : 'später'} ›
      </button>
      {letzter && letzter !== tabelle.am && (
        <button className="leise" onClick={() => onWahl(null)}>
          letzter Tag ({datum(letzter)})
        </button>
      )}
    </div>
  )
}

/** Was hervorgehoben ist, in Worten - Farbe trägt nie allein. Steht die Spalte nicht auf dem Blatt, sagt die Zeile das. */
function AuswahlZeile({ tabelle, auswahl, onAufheben }: { tabelle: NotierungTabelle; auswahl: NotierungAuswahl; onAufheben: () => void }) {
  const spalte = tabelle.spalten.find((s) => s.quelle_id === auswahl.spalte)
  const monate = [...auswahl.monate].sort()
  const monateText =
    monate.length === 0 ? '' : monate.length === 1 ? liefermonat(monate[0]) : `${liefermonat(monate[0])} bis ${liefermonat(monate[monate.length - 1])}`
  return (
    <div className="leiste nz-auswahl">
      {spalte ? (
        <span>
          Ausgewählt: <b>{spalte.bezeichnung}</b> ({spalte.rohstoff}){monateText && <>, Lieferung {monateText}</>} - Rahmen um die Spalte,
          Balken am Monat.
        </span>
      ) : (
        <span>Die gewählte Spalte steht nicht auf diesem Blatt - sie ist vielleicht nicht aktiv.</span>
      )}
      <button className="leise" onClick={onAufheben}>
        Auswahl aufheben
      </button>
    </div>
  )
}

/** Die Preisspannen der Vorjahre unter der Tabelle, je Rohstoff ein Kasten - wie unten in der Mappe. */
function Preisspannen({ spannen }: { spannen: NotierungPreisspanne[] }) {
  if (spannen.length === 0) return null
  const jeRohstoff = new Map<string, NotierungPreisspanne[]>()
  for (const s of spannen) jeRohstoff.set(s.rohstoff, [...(jeRohstoff.get(s.rohstoff) ?? []), s])
  return (
    <div className="nz-spannen">
      {[...jeRohstoff.entries()].map(([rohstoff, liste]) => (
        <div key={rohstoff} className="karte nz-spanne">
          <h3>Preisspannen {rohstoff}</h3>
          <div className="tabellenrahmen">
            <table>
              <thead>
                <tr>
                  <th>Jahr</th>
                  <th className="zahl">von €/t</th>
                  <th className="zahl">bis €/t</th>
                  <th>Herkunft</th>
                </tr>
              </thead>
              <tbody>
                {[...liste]
                  .sort((a, b) => b.jahr - a.jahr)
                  .map((s) => (
                    <tr key={s.jahr}>
                      <td>{s.jahr}</td>
                      <td className="zahl">{eurT(s.von)}</td>
                      <td className="zahl">{eurT(s.bis)}</td>
                      <td className="hinweis">
                        {s.quelle === 'excel' ? 'aus der Mappe' : 'aus den Notierungen'}
                        {s.bemerkung ? ` - ${s.bemerkung}` : ''}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}
